// Single source for the GA4 gtag bootstrap. RootLayout is the root layout, so rendering this once
// there covers every page. Three other layouts carried their own copy: CatalogLayout, which IS routed
// (`public/CatalogLayout`) and so double-fired gtag on catalog pages, plus PublicLayout and
// LandingLayout, which are orphaned (wired to no route). All three copies are gone.
//
// The measurement id is a PUBLIC value (visible in page source), so it stays a hardcoded const
// rather than an env var — that keeps GA free of a deploy dependency that could silently disable it.
export const GA_MEASUREMENT_ID = "G-69M74R4690";

// GA only loads on the real production hosts. Everything else — localhost, the folio-qa PROD build,
// preview deploys — matches nothing, so no gtag loads and no `/g/collect` request is ever made,
// keeping test traffic out of the production property. The check is client-side against the browser
// hostname (not a server header), so it also survives Azure Front Door rewriting the origin Host.
//
// Two kinds of prod host:
// - `GA_PROD_HOSTS` is matched EXACTLY (folioai.com has no GA-bearing subdomains).
// - `GA_PROD_HOST_SUFFIXES` covers `folio.shop`'s wildcard buyer storefronts (`luxelife.folio.shop`,
//   …). The leading dot is a security boundary: it makes `foo.folio.shop` match while `evilfolio.shop`
//   does NOT (it ends with `folio.shop` but not `.folio.shop`). `folio.shop` itself is in the exact
//   list. The storefront app (apps/store) carries no GA today, so the suffix is forward-looking.
export const GA_PROD_HOSTS = ["folioai.com", "www.folioai.com", "folio.shop"];
export const GA_PROD_HOST_SUFFIXES = [".folio.shop"];

// Pages whose URL carries something that must not reach GA, which records the full URL, query string
// included: the unsubscribe and order track tokens hold the recipient's address. Matched on the path's
// end so a locale prefix doesn't slip past.
export const GA_EXCLUDED_PATHS = ["/unsubscribe", "/orders/track"];

// Query parameters with the same problem on any page: the sign-up opened from an order track link
// carries that token.
export const GA_EXCLUDED_PARAMS = ["track"];

// The inline bootstrap, as a string. On a prod host it defines `window.gtag` synchronously during
// HTML parse (before hydration) — the contract SubscriptionCallback relies on — and injects the
// gtag loader. On any other host it returns before touching anything, so gtag stays undefined and
// that view's `typeof gtag !== "function"` branch settles immediately. Kept as an exported builder
// so the actual shipped script is executable under test, not just asserted as markup.
export function buildGaBootstrap(): string {
  return `(function(){
  var h = location.hostname;
  var ok = ${JSON.stringify(GA_PROD_HOSTS)}.indexOf(h) !== -1
    || ${JSON.stringify(GA_PROD_HOST_SUFFIXES)}.some(function(sfx){ return h.length > sfx.length && h.slice(-sfx.length) === sfx; });
  var p = location.pathname || '';
  if (${JSON.stringify(GA_EXCLUDED_PATHS)}.some(function(x){ return p.slice(-x.length) === x; })) return;
  var q = new URLSearchParams(location.search);
  if (${JSON.stringify(GA_EXCLUDED_PARAMS)}.some(function(x){ return q.has(x); })) return;
  if (!ok) {
    // Leave a breadcrumb so a silent GA outage on a new-but-unlisted prod host has a reason in the
    // console instead of just missing data. This is the only signal, so keep it.
    if (window.console && console.debug) console.debug('[ga] silenced on host ' + h);
    return;
  }
  var s = document.createElement('script');
  s.async = true;
  s.src = 'https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}';
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', '${GA_MEASUREMENT_ID}');
})();`;
}

export function GoogleAnalytics() {
  return <script dangerouslySetInnerHTML={{ __html: buildGaBootstrap() }} />;
}
